import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronRight, LuSearchX } from 'react-icons/lu';
import Button from '../ui/Button';
import { useTranslation } from '../../i18n/LanguageContext';

const SKELETON_CARDS = 6;

const rowBackground = (selected, flashing) => {
  if (flashing) return 'tw-bg-amber-100 dark:tw-bg-[#433d2d]';
  if (selected) return 'tw-bg-teal-50 tw-shadow-[inset_3px_0_0_#14b8a6] dark:tw-bg-[#20383d]';
  return 'tw-bg-transparent active:tw-bg-slate-900/[0.04] dark:active:tw-bg-white/[0.05]';
};

/**
 * The list of a data page on narrow screens (phones, or a tablet beside the
 * sidebar): one tappable row per record. `renderCard(row)` draws the row's
 * content; tapping selects the record (the page opens its detail panel).
 */
const DataCardList = ({ label, rows, getRowId = (row) => row.id, renderCard, selectedId, flashId, onSelect, isLoading, isBusy, emptyLabel, canClearFilters, onClearFilters }) => {
  const { t } = useTranslation();

  if (isLoading && rows.length === 0) {
    return (
      <ul aria-label={t('dataTable.table.loading')} className="tw-divide-y tw-divide-slate-100 tw-border-t tw-border-slate-200/70 dark:tw-divide-white/[0.06] dark:tw-border-white/10">
        {Array.from({ length: SKELETON_CARDS }, (_, index) => (
          <li key={index} className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-4">
            <span className="tw-h-10 tw-w-10 tw-shrink-0 tw-animate-pulse tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10" />
            <span className="tw-flex-1 tw-space-y-2">
              <span className="tw-block tw-h-3 tw-w-2/5 tw-animate-pulse tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10" />
              <span className="tw-block tw-h-3 tw-w-3/5 tw-animate-pulse tw-rounded-full tw-bg-slate-200/60 dark:tw-bg-white/[0.07]" />
            </span>
          </li>
        ))}
      </ul>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-border-t tw-border-slate-200/70 tw-px-6 tw-py-12 tw-text-center dark:tw-border-white/10">
        <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
          <LuSearchX aria-hidden="true" className="tw-h-7 tw-w-7" />
        </span>
        <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{emptyLabel}</p>
        {canClearFilters && <Button size="sm" onClick={onClearFilters}>{t('dataTable.table.clearFilters')}</Button>}
      </div>
    );
  }

  return (
    <ul
      aria-label={label}
      className={`tw-divide-y tw-divide-slate-100 tw-border-t tw-border-slate-200/70 tw-transition-opacity tw-duration-200 dark:tw-divide-white/[0.06] dark:tw-border-white/10 ${isBusy ? 'tw-opacity-60' : 'tw-opacity-100'}`}
    >
      {rows.map((row) => {
        const id = getRowId(row);
        const selected = id === selectedId;
        return (
          <li key={id} className="tw-block">
            <button
              type="button"
              data-row-id={id}
              aria-pressed={selected}
              onClick={() => onSelect(row)}
              className={`tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-left tw-transition-colors tw-duration-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/60 ${rowBackground(selected, id === flashId)}`}
            >
              {renderCard(row)}
              <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-300 dark:tw-text-slate-600" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

DataCardList.propTypes = {
  label: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  getRowId: PropTypes.func,
  renderCard: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  flashId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isBusy: PropTypes.bool,
  emptyLabel: PropTypes.string.isRequired,
  canClearFilters: PropTypes.bool,
  onClearFilters: PropTypes.func.isRequired,
};

export default DataCardList;
